import { useState } from 'react'
import PropTypes from 'prop-types'
import apiService from '../services/api'

const IdeaFormWizard = ({ onSuccess, onCancel }) => {
  const [currentStep, setCurrentStep] = useState(1)
  const [formData, setFormData] = useState({
    what: '',
    who: '',
    why: '',
    what_expected: '',
    make_money: false,
    positive_examples: '',
    negative_examples: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const totalSteps = 4

  const stepConfig = {
    1: {
      title: "Core Concept",
      description: "Define what you're building and who it's for",
      fields: ['what', 'who'],
      requiredFields: ['what', 'who']
    },
    2: {
      title: "Value Proposition", 
      description: "Explain why this matters and what outcomes you expect",
      fields: ['why', 'what_expected'],
      requiredFields: ['why']
    },
    3: {
      title: "Commercial Intent",
      description: "Tell us about your monetization goals",
      fields: ['make_money'],
      requiredFields: []
    },
    4: {
      title: "Market Insights",
      description: "Share examples of what works and what doesn't",
      fields: ['positive_examples', 'negative_examples'],
      requiredFields: []
    }
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
    setError('') // Clear error when user starts typing
  }

  const validateStep = (step) => {
    const config = stepConfig[step]
    const errors = []
    
    config.requiredFields.forEach(field => {
      if (!formData[field] || formData[field].trim() === '') {
        errors.push(`${field} is required`)
      }
    })
    
    return errors
  }

  const handleNext = () => {
    const errors = validateStep(currentStep)
    if (errors.length > 0) {
      setError(`Please fill in all required fields before continuing.`)
      return
    }
    
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1)
      setError('')
    }
  }

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
      setError('')
    }
  }

  const handleSubmit = async () => {
    // Validate all steps before final submission
    for (let step = 1; step <= totalSteps; step++) {
      const errors = validateStep(step)
      if (errors.length > 0) {
        setCurrentStep(step)
        setError(`Please complete all required fields before submitting.`)
        return
      }
    }

    setIsSubmitting(true)
    setError('')
    setSuccess('')

    try {
      const response = await apiService.createIdea(formData)
      
      if (response.idea_id) {
        setSuccess('Idea created successfully! Redirecting to your ideas...')
        
        // Reset form
        setFormData({ 
          what: '', 
          who: '', 
          why: '', 
          what_expected: '', 
          make_money: false, 
          positive_examples: '', 
          negative_examples: '' 
        })
        setCurrentStep(1)
        
        setTimeout(() => {
          onSuccess?.(response)
        }, 1500)
      }
    } catch (err) {
      setError('Failed to create idea. Please try again.')
      console.error('Create idea error:', err)
    } finally {
      setIsSubmitting(false)
    }
  }

  const renderProgressBar = () => {
    return (
      <div className="mb-8 md:mb-12">
        <div className="flex items-center justify-between mb-3 md:mb-4 relative px-2 md:px-0">
          {/* Render connector lines first as background */}
          {[1, 2, 3].map((step) => (
            <div
              key={`line-${step}`}
              className={`absolute h-0.5 md:h-1 transition-all duration-300 ${
                step < currentStep ? 'bg-blue-500' : 'bg-gray-200 dark:bg-gray-700'
              }`}
              style={{
                left: `calc(${(step - 1) * 33.33}% + 16px)`,
                right: `calc(${(3 - step) * 33.33}% + 16px)`,
                top: '50%',
                transform: 'translateY(-50%)'
              }}
            />
          ))}
          
          {/* Render step circles on top */}
          {[1, 2, 3, 4].map((step) => (
            <div
              key={step}
              className={`w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center text-xs md:text-sm font-bold transition-all duration-300 relative z-10 ${
                step <= currentStep
                  ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-lg'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
              }`}
            >
              {step < currentStep ? (
                <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                step
              )}
            </div>
          ))}
        </div>
        <div className="text-center px-4 md:px-0">
          <h3 className="text-lg md:text-xl font-bold text-white">
            <span className="hidden md:inline">Step {currentStep}: </span>
            <span className="md:hidden">{currentStep}. </span>
            {stepConfig[currentStep].title}
          </h3>
          <p className="text-gray-300 text-sm md:text-base mt-1">
            {stepConfig[currentStep].description}
          </p>
        </div>
      </div>
    )
  }

  const renderField = (fieldName) => {
    const fieldConfigs = {
      what: {
        label: "What's your idea?",
        description: "Describe your core concept, product, or service idea clearly and concisely.",
        placeholder: "Example: An AI-powered indoor garden system that automatically monitors and cares for plants using computer vision and IoT sensors...",
        gradient: "from-blue-500 to-indigo-500",
        focusRing: "focus:ring-blue-500 focus:border-blue-500"
      },
      who: {
        label: "Who's your target audience?",
        description: "Define your ideal customers, users, or beneficiaries. Be specific about demographics and needs.",
        placeholder: "Example: Urban professionals aged 25-40 who live in apartments without outdoor space but want to grow fresh herbs and vegetables...",
        gradient: "from-emerald-500 to-teal-500",
        focusRing: "focus:ring-emerald-500 focus:border-emerald-500"
      },
      why: {
        label: "Why is this valuable?",
        description: "Explain the problem you're solving, the value you're creating, and why people will care.",
        placeholder: "Example: Most people want to grow their own food but fail due to lack of knowledge and time. This system eliminates the guesswork...",
        gradient: "from-purple-500 to-pink-500",
        focusRing: "focus:ring-purple-500 focus:border-purple-500"
      },
      what_expected: {
        label: "What do you expect them to achieve using this?",
        description: "Describe the specific outcomes, goals, or benefits your users will experience.",
        placeholder: "Example: Users will achieve self-sufficiency in fresh herbs and vegetables, save money on organic produce...",
        gradient: "from-orange-500 to-red-500",
        focusRing: "focus:ring-orange-500 focus:border-orange-500"
      },
      make_money: {
        label: "Do you want to make money from this idea?",
        description: "This helps us understand your commercial intentions and provide relevant guidance.",
        gradient: "from-green-500 to-emerald-500"
      },
      positive_examples: {
        label: "What positive examples can you share?",
        description: "Share existing products you like and how they should inform your creation.",
        placeholder: "Example: I love how the Nest thermostat learns user behavior and adjusts automatically...",
        gradient: "from-teal-500 to-cyan-500",
        focusRing: "focus:ring-teal-500 focus:border-teal-500"
      },
      negative_examples: {
        label: "What negative examples can you share?",
        description: "Share similar products that fail or miss the mark and how you'd avoid their mistakes.",
        placeholder: "Example: Existing hydroponic systems are too complex and expensive for average consumers...",
        gradient: "from-rose-500 to-pink-500",
        focusRing: "focus:ring-rose-500 focus:border-rose-500"
      }
    }

    const config = fieldConfigs[fieldName]
    const isRequired = stepConfig[currentStep].requiredFields.includes(fieldName)

    if (fieldName === 'make_money') {
      return (
        <div key={fieldName} className="space-y-4 md:space-y-6">
          <div className="flex items-start md:items-center space-x-3">
            <div className={`w-8 h-8 bg-gradient-to-r ${config.gradient} rounded-lg flex items-center justify-center flex-shrink-0`}>
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
              </svg>
            </div>
            <div className="flex-1">
              <label htmlFor={fieldName} className="text-base md:text-lg font-semibold text-white">
                {config.label}
                {isRequired && <span className="text-red-500 ml-1">*</span>}
              </label>
              <p className="text-sm md:text-base text-gray-300 mt-1">{config.description}</p>
            </div>
          </div>
          <div className="md:ml-11">
            <label className="flex items-center space-x-3 cursor-pointer p-4 md:p-6 bg-gradient-to-r from-gray-800 to-gray-700 rounded-xl hover:from-gray-700 hover:to-gray-600 transition-all duration-200 border border-gray-600">
              <input
                id={fieldName}
                name={fieldName}
                type="checkbox"
                checked={formData[fieldName]}
                onChange={handleChange}
                className="w-5 h-5 text-green-600 bg-gray-800 border-gray-600 rounded focus:ring-green-500 focus:ring-2 transition-all duration-200"
              />
              <span className="text-sm text-gray-300 select-none font-medium">
                Yes, I want to commercialize this idea
              </span>
            </label>
          </div>
          <div className="md:ml-11 p-3 md:p-4 bg-blue-900/30 rounded-xl border border-blue-800">
            <p className="text-xs md:text-sm text-blue-300">
              <span className="font-semibold">💡 Tip:</span> Selecting this option will help us provide targeted advice on monetization strategies, business models, and market positioning for your idea.
            </p>
          </div>
        </div>
      )
    }

    return (
      <div key={fieldName} className="space-y-3 md:space-y-4">
        <div className="flex items-start md:items-center space-x-3">
          <div className={`w-8 h-8 bg-gradient-to-r ${config.gradient} rounded-lg flex items-center justify-center flex-shrink-0`}>
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
          </div>
          <div className="flex-1">
            <label htmlFor={fieldName} className="text-base md:text-lg font-semibold text-white">
              {config.label}
              {isRequired && <span className="text-red-500 ml-1">*</span>}
            </label>
            <p className="text-sm md:text-base text-gray-300 mt-1">{config.description}</p>
          </div>
        </div>
        <div className="md:ml-11">
          <textarea
            id={fieldName}
            name={fieldName}
            value={formData[fieldName]}
            onChange={handleChange}
            required={isRequired}
            rows={4}
            className={`w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-600 rounded-xl shadow-sm placeholder-gray-500 focus:outline-none focus:ring-2 ${config.focusRing} bg-gray-900/50 backdrop-blur-sm transition-all duration-200 text-base resize-none text-white`}
            placeholder={config.placeholder}
          />
        </div>
      </div>
    )
  }

  const renderCurrentStep = () => {
    const currentStepFields = stepConfig[currentStep].fields

    return (
      <div className="space-y-6 md:space-y-8 min-h-[200px] md:min-h-[300px] mb-8">
        {currentStepFields.map(fieldName => renderField(fieldName))}
      </div>
    )
  }

  const renderNavigation = () => {
    return (
      <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between pt-8 md:pt-10 border-t border-gray-200/50 space-y-4 md:space-y-0 md:relative">
        <div className="flex items-center justify-between md:justify-start md:space-x-4 md:w-32">
          {currentStep === 1 ? (
            <button
              type="button"
              onClick={onCancel}
              className="inline-flex items-center justify-center px-4 md:px-6 py-2.5 md:py-3 border border-gray-600 shadow-sm text-sm font-medium rounded-xl text-gray-300 bg-gray-800 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-all duration-200 flex-1 md:flex-initial"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Cancel
            </button>
          ) : (
            <button
              type="button"
              onClick={handlePrevious}
              className="inline-flex items-center justify-center px-4 md:px-6 py-2.5 md:py-3 border border-gray-600 shadow-sm text-sm font-medium rounded-xl text-gray-300 bg-gray-800 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 flex-1 md:flex-initial"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Previous
            </button>
          )}
        </div>

        <div className="text-center order-first md:order-none mb-2 md:mb-0 md:absolute md:left-1/2 md:-translate-x-1/2">
          <span className="text-xs md:text-sm text-gray-300 font-medium">
            Step {currentStep} of {totalSteps}
          </span>
        </div>

        <div className="flex items-center justify-end space-x-3 md:space-x-4 flex-1 md:flex-initial md:w-32">
          {currentStep < totalSteps ? (
            <button
              type="button"
              onClick={handleNext}
              className="group inline-flex items-center justify-center px-4 md:px-6 py-2.5 md:py-3 border border-transparent text-sm font-medium rounded-xl text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 flex-1 md:flex-initial"
            >
              Next
              <svg className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="group relative inline-flex items-center justify-center px-6 md:px-8 py-2.5 md:py-3 border border-transparent text-sm font-medium rounded-xl text-white bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:transform-none disabled:hover:shadow-lg flex-1 md:flex-initial whitespace-nowrap"
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Creating Idea...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Create Idea
                </>
              )}
            </button>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8">
      <div className="rounded-2xl bg-gradient-to-br from-gray-700/50 to-gray-700/50 shadow-none outline -outline-offset-1 outline-white/10 px-5 py-6 sm:px-6 relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent pointer-events-none"></div>
        <div className="absolute top-0 left-0 w-96 h-40 bg-gradient-to-br from-green-900/20 via-transparent to-transparent rounded-tl-2xl pointer-events-none"></div>
        <div className="absolute bottom-0 right-0 w-64 h-32 bg-gradient-to-tl from-green-900/20 via-transparent to-transparent rounded-br-2xl pointer-events-none"></div>
        
        <div className="p-4 relative z-10">
          <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center shadow-lg">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="size-6 text-white">
                <path d="M12 4.5v15m7.5-7.5h-15" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            Create New Idea
          </h2>
          <p className="text-lg text-gray-400 mb-12">
            Transform your inspiration into actionable insights. We&apos;ll guide you through each step.
          </p>
        {error && (
          <div className="bg-red-50 dark:bg-red-900/30 border-b border-red-200 p-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700 dark:text-red-300 font-medium">{error}</p>
              </div>
            </div>
          </div>
        )}
        
        {success && (
          <div className="bg-green-900/30 border-b border-green-700 p-6 mb-8">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-green-300 font-medium">{success}</p>
              </div>
            </div>
          </div>
        )}

          {renderProgressBar()}
          {renderCurrentStep()}
          {renderNavigation()}
        </div>
      </div>
    </div>
  )
}

IdeaFormWizard.propTypes = {
  onSuccess: PropTypes.func,
  onCancel: PropTypes.func.isRequired
}

export default IdeaFormWizard